import React from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../constants/svgIcon';
import {
  FingerWritingAnswer,
  TextOrderingAnswer,
} from '../../../models/GameStore';
import { SvgIcon, Switch, TTSPlayer } from '../../Molecules';
import { GameQuestionPanel, TextOrderingGameMode } from '../GameQuestionPanel';
import { HandwritingBlankInfo } from '../HandwritingGameMainGroup';
import {
  HandwritingGameMode,
  HandwritingGameQuestionPanel,
} from '../HandwritingGameQuestionPanel';
import { TextOrderingBlankInfo } from '../MemoryGameMainGroup';

import styles from './MemoryHelperPanel.module.css';

type TextOrderingAnswerHelper = {
  textAnswer: TextOrderingAnswer;
  writingAnswer?: never;
  blankInfos: Record<number, TextOrderingBlankInfo>;
  mode: TextOrderingGameMode;
};

type FingerWritingAnswerHelper = {
  writingAnswer: FingerWritingAnswer;
  textAnswer?: never;
  blankInfos: Record<number, HandwritingBlankInfo>;
  mode: HandwritingGameMode;
};

export type MemoryHelperQuestionPanel =
  | TextOrderingAnswerHelper
  | FingerWritingAnswerHelper
  | undefined;

export type MemoryHelperPanelProps = {
  questionPanel: MemoryHelperQuestionPanel;
  isUseTranslate: boolean;
  originalLanguage: string;
  translatedLanguage: string;
  originalAudioTts: string | null | undefined;
  translatedAudioTts: string | null | undefined;
  translationResult: string | undefined;
  isGameStart: boolean;
  onSwitchTranslate: () => void;
  onClickLanguage: () => void;
  onClickMemoryHelper?: () => void;
};

export const MemoryHelperPanel: React.FC<MemoryHelperPanelProps> = ({
  questionPanel,
  isUseTranslate,
  originalLanguage,
  translatedLanguage,
  originalAudioTts,
  translatedAudioTts,
  translationResult,
  isGameStart,
  onSwitchTranslate,
  onClickLanguage,
  onClickMemoryHelper,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={`${styles.container} ${
        onClickMemoryHelper ? styles.gameStartContainer : ''
      }`}
      onClick={onClickMemoryHelper}
    >
      {onClickMemoryHelper && (
        <div className={styles.memoryHelper}>
          <div className={styles.memoryHelperRow}>
            <SvgIcon
              name={SVGIconType.MemoryHelper}
              width={24}
              height={24}
              color="white"
            />
            <p className={styles.memoryHelperLabel}>{t('Memory Helper')}</p>
            <SvgIcon
              name={SVGIconType.Arrow}
              color="white"
              width={16}
              height={16}
            />
          </div>
        </div>
      )}
      <div className={styles.googleTranslate}>
        <div className={styles.googleTranslateRow}>
          <SvgIcon
            name={SVGIconType.Translation}
            width={24}
            height={24}
            color="white"
          />
          <p className={styles.googleTranslateLabel}>{t('Translation')}</p>
          <Switch
            checked={isUseTranslate}
            label={isUseTranslate ? 'ON' : 'OFF'}
            onChange={onSwitchTranslate}
          />
        </div>

        <div className={styles.divider} />

        {isUseTranslate ? (
          <div className={styles.languageSelectRow}>
            <p className={styles.languageFromName}>{t(originalLanguage)}</p>
            <SvgIcon
              name={SVGIconType.DirectArrow}
              width={24}
              height={24}
              color="white"
            />
            <div className={styles.languageToName} onClick={onClickLanguage}>
              <p>{t(translatedLanguage)}</p>
              <SvgIcon
                name={SVGIconType.Triangle}
                color="white"
                width={20}
                height={20}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className={styles.textContentContainer}>
        <div className={styles.textContentItem}>
          {originalAudioTts ? (
            <div className={styles.ttsPlayer}>
              <TTSPlayer audioSrc={originalAudioTts} />
            </div>
          ) : null}

          {questionPanel?.textAnswer ? (
            <GameQuestionPanel
              answer={questionPanel.textAnswer}
              currentBlankIndex={-1}
              spellingPreFilledIndexes={[]}
              blankInfos={questionPanel.blankInfos}
              mode={questionPanel.mode}
              useColor={false}
              isLearner={true}
              isGameStart={isGameStart}
            />
          ) : questionPanel?.writingAnswer ? (
            <HandwritingGameQuestionPanel
              answer={questionPanel.writingAnswer}
              currentBlankIndex={-1}
              blankInfos={questionPanel.blankInfos}
              mode={questionPanel.mode}
              isLearner={true}
              isGameStart={isGameStart}
            />
          ) : null}
        </div>

        {isUseTranslate && translationResult ? (
          <>
            <div className={styles.textContentDivider} />
            <div className={styles.textContentItem}>
              {translatedAudioTts ? (
                <div className={styles.ttsPlayer}>
                  <TTSPlayer audioSrc={translatedAudioTts} />
                </div>
              ) : null}

              <p className={`${styles.textContent}`}>{translationResult}</p>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};
